'use client'

/* OrbitFlow primary navigation — collapsible left sidebar.
   1:1 with the Claude Design "Orbit Flow" handoff: hamburger toggle next to
   brand mark, search + new quick row, grouped nav with accent dots + rails,
   and a flashy XP footer card. Auto-collapses on narrow viewports. */

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  Search, LogOut, AlertTriangle, Clock as ClockIcon, Settings as SettingsIcon,
  Menu, Plus,
} from 'lucide-react'
import { useTranslations } from '@/lib/i18n'
import { useAuth } from '@/lib/stores/auth-store'
import { useTimeTracking } from '@/lib/stores/time-tracking-store'
import { usePermissions } from '@/lib/hooks/use-permissions'
import { CommandModal } from './command-modal'
import { HelpSidebarContent } from './help-sidebar-content'
import { NAV_SECTIONS, activeSection, activePage } from './nav-config'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/* Brand mark — same orbit shape as OrbitLoader: ring (r=44) with a satellite
   circle at the top-left gap. The gap is created by stroke-dasharray so the
   shape renders identically to the loader (just without the spin). */
function BrandMark({ size = 36 }: { size?: number }) {
  const C = 2 * Math.PI * 44          // 276.46
  const gap = 50.8
  const dash = +(C - gap).toFixed(2)  // 225.66
  const dashOffset = 82
  return (
    <svg viewBox="0 0 128 128" width={size} height={size} fill="none" aria-hidden>
      <defs>
        <linearGradient id="ofSidebarMark" x1="19" y1="13" x2="109" y2="115" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="hsl(var(--primary))" stopOpacity="0.7" />
          <stop offset="55%"  stopColor="hsl(var(--primary))" stopOpacity="1" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="1" />
        </linearGradient>
      </defs>
      <circle
        cx="64" cy="64" r="44"
        stroke="url(#ofSidebarMark)"
        strokeWidth="16"
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={dashOffset}
      />
      <circle
        cx="29" cy="34" r="14"
        stroke="url(#ofSidebarMark)"
        strokeWidth="16"
      />
    </svg>
  )
}

/* XP progress ring with avatar inside */
function XpRing({ pct = 0.62, size = 38, stroke = 3 }: { pct?: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--of-xp-ring-track)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="var(--of-violet)" strokeWidth={stroke}
        strokeDasharray={`${c * pct} ${c}`} strokeLinecap="round"
      />
    </svg>
  )
}

interface SidebarProps {
  /** True when the mobile drawer is open (controlled by MainLayout). */
  mobileOpen?: boolean
  /** Closes the mobile drawer (e.g. after the user picks a nav item). */
  onMobileClose?: () => void
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps = {}) {
  const t = useTranslations()
  const pathname = usePathname()
  const { user, logout, session } = useAuth()
  const { activeTimer, getFormattedTime } = useTimeTracking()
  const { isAdmin } = usePermissions()

  // Resolve a SINGLE active nav item (the most-specific match) so the sidebar
  // can never light up multiple — or every — item, regardless of how hrefs
  // overlap or what transient pathname value React hands us.
  const activeHref = useMemo(() => {
    const section = activeSection(pathname)
    return (section && activePage(section, pathname)?.href) ?? null
  }, [pathname])

  const [collapsed, setCollapsed] = useState<boolean>(false)
  const [showCommand, setShowCommand] = useState(false)
  const [showHelpSidebar, setShowHelpSidebar] = useState(false)
  const [showLogoutAlert, setShowLogoutAlert] = useState(false)

  // Restore collapsed state from localStorage on first paint
  useEffect(() => {
    try {
      const v = window.localStorage.getItem('of:sidebar:collapsed')
      if (v === '1') setCollapsed(true)
    } catch {}
  }, [])

  useEffect(() => {
    try { window.localStorage.setItem('of:sidebar:collapsed', collapsed ? '1' : '0') } catch {}
    // Sync to body so the main content (which can't read this state directly)
    // can match its margin-left to the sidebar width via CSS.
    if (typeof document !== 'undefined') {
      if (collapsed) document.body.setAttribute('data-sb-collapsed', '1')
      else document.body.removeAttribute('data-sb-collapsed')
    }
  }, [collapsed])

  // Clean up the body attribute when the sidebar unmounts (route changes to
  // an auth-only page that doesn't render the sidebar).
  useEffect(() => () => {
    if (typeof document !== 'undefined') document.body.removeAttribute('data-sb-collapsed')
  }, [])

  // Session expiry tick
  useEffect(() => {
    const check = () => {
      if (session?.expiresAt && Date.now() >= session.expiresAt) {
        const { handleAuthError } = require('@/lib/api-error-handler')
        handleAuthError({ message: 'Session expired' })
      }
    }
    check()
    const id = setInterval(check, 30000)
    return () => clearInterval(id)
  }, [session?.expiresAt])

  // ⌘K opens search; "M" toggles collapse (ignored while typing)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null
      const tag = (tgt?.tagName || '').toLowerCase()
      const editable = tag === 'input' || tag === 'textarea' || (tgt as any)?.isContentEditable
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setShowCommand((v) => !v)
      } else if (!editable && !e.metaKey && !e.ctrlKey && e.key.toLowerCase() === 'm') {
        e.preventDefault()
        setCollapsed((v) => !v)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const handleLogout = () => {
    if (activeTimer) {
      setShowLogoutAlert(true)
      return
    }
    logout()
  }

  const initials = user
    ? user.firstname && user.lastname
      ? `${user.firstname[0]}${user.lastname[0]}`
      : (user.login || 'U')[0].toUpperCase()
    : 'U'

  const displayName = user
    ? user.firstname && user.lastname
      ? `${user.firstname} ${user.lastname}`
      : user.login || 'User'
    : ''

  // Static placeholder until gamification data lands — keeps the flashy footer alive
  const xpPct = 0.62
  const xpTier = 'Comet'
  const xpLevel = 14

  return (
    <>
      {/* Timer-running logout warning */}
      <AlertDialog open={showLogoutAlert} onOpenChange={setShowLogoutAlert}>
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/20">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <AlertDialogTitle className="text-xl">{t('header.timerRunning')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base space-y-4 pt-4">
              <div className="bg-muted p-4 rounded-lg space-y-3">
                <div className="flex items-start gap-2">
                  <ClockIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      #{activeTimer?.issueId}: {activeTimer?.issueSubject}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Activity: {activeTimer?.activityName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-sm text-muted-foreground">{t('header.elapsedTime')}:</span>
                  <span className="text-lg font-mono font-bold text-primary">{getFormattedTime()}</span>
                </div>
              </div>
              <p className="text-foreground">{t('header.pleaseLogTime')}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <aside
        className={`of-sidebar${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-mobile-open' : ''}`}
        aria-label="Primary navigation"
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        {/* ── Brand row: hamburger toggle + orbital glyph + name/meta ── */}
        <div className="of-sb-brand">
          <button
            type="button"
            className="of-sb-menu"
            onClick={() => {
              // On mobile the same button dismisses the drawer; on desktop it
              // toggles the collapsed/expanded state.
              if (mobileOpen) onMobileClose?.()
              else setCollapsed((v) => !v)
            }}
            aria-label={mobileOpen ? 'Close menu' : (collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
            title={mobileOpen ? 'Close menu' : (collapsed ? 'Expand sidebar (M)' : 'Collapse sidebar (M)')}
          >
            <Menu size={18} strokeWidth={1.8} />
          </button>
          <Link href="/" className="of-sb-brand-link" aria-label="OrbitFlow home">
            <span className="of-sb-mark" aria-hidden>
              <BrandMark size={36} />
            </span>
            <span className="of-sb-brand-text">
              <span className="of-sb-brand-name">Orbit Flow</span>
              <span className="of-sb-brand-meta">Workspace · Pro</span>
            </span>
          </Link>
        </div>

        {/* ── Quick row: search + new ── */}
        <div className="of-sb-quick">
          <button
            type="button"
            className="of-sb-search"
            onClick={() => setShowCommand(true)}
            aria-label="Open search"
          >
            <Search size={14} />
            <span className="of-sb-search-label">Search</span>
            <span className="of-sb-kbd">⌘K</span>
          </button>
          <Link href="/issues" className="of-sb-new" title="New task" aria-label="New task">
            <Plus size={14} strokeWidth={2} />
          </Link>
        </div>

        {/* ── Nav scroll ── */}
        <nav className="of-sb-scroll" aria-label="Sections">
          {NAV_SECTIONS.map((section) => {
            const pages = section.pages.filter((p) => !p.adminOnly || isAdmin)
            if (pages.length === 0) return null
            return (
              <div
                key={section.id}
                className="of-sb-group"
                style={{ ['--of-sb-group-c' as any]: section.accent } as React.CSSProperties}
              >
                {section.name && (
                  <div className="of-sb-group-label">
                    <span className="of-sb-group-dot" style={{ background: section.accent }} aria-hidden />
                    <span>{section.name}</span>
                  </div>
                )}
                <div className="of-sb-group-items">
                  {pages.map((page) => {
                    const PageIcon = page.icon
                    const active = page.href === activeHref
                    return (
                      <Link
                        key={page.href}
                        href={page.href}
                        prefetch={false}
                        title={page.name}
                        onClick={() => onMobileClose?.()}
                        className={`of-sb-item${active ? ' is-active' : ''}`}
                        style={
                          active
                            ? ({ ['--of-sb-accent' as any]: section.accent } as React.CSSProperties)
                            : undefined
                        }
                      >
                        <span className="of-sb-item-rail" aria-hidden />
                        <span className="of-sb-item-ic" aria-hidden>
                          <PageIcon size={15} strokeWidth={1.7} />
                        </span>
                        <span className="of-sb-item-label">{page.name}</span>
                        {page.badge && <span className="of-sb-item-badge">{page.badge}</span>}
                      </Link>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </nav>

        {/* ── Footer: flashy XP / user card ── */}
        <div className="of-sb-foot">
          <div className="of-sb-xp">
            <div className="of-sb-xp-glow" aria-hidden />
            <div className="of-sb-xp-row">
              <div className="of-sb-ring-wrap">
                <XpRing pct={xpPct} size={38} stroke={3} />
                <div className="of-sb-ring-av">{initials}</div>
              </div>
              <div className="of-sb-xp-text">
                <span className="of-sb-xp-name">{displayName || 'You'}</span>
                <span className="of-sb-xp-meta">
                  Lvl {xpLevel} · {xpTier} · {Math.round(xpPct * 100)}%
                </span>
              </div>
              <Link
                href="/settings"
                className="of-sb-cog"
                aria-label={t('common.settings')}
                title={t('common.settings')}
                onClick={(e) => e.stopPropagation()}
              >
                <SettingsIcon size={13} />
              </Link>
            </div>
            <div className="of-sb-xp-bar">
              <div style={{ width: `${Math.round(xpPct * 100)}%` }} />
            </div>
          </div>

          {/* Sign-out only visible when expanded — keeps collapsed bar tidy */}
          {user && (
            <button
              type="button"
              className="of-sb-signout"
              onClick={handleLogout}
              aria-label={t('common.signOut')}
              title={t('common.signOut')}
            >
              <LogOut size={13} />
              <span>{t('common.signOut')}</span>
            </button>
          )}
        </div>
      </aside>

      <CommandModal open={showCommand} onClose={() => setShowCommand(false)} />
      <HelpSidebarContent isOpen={showHelpSidebar} onClose={() => setShowHelpSidebar(false)} />
    </>
  )
}
